/**
 * outreachLogic — every recruiter-facing word for outreach lists, plus the pure math the wizard runs
 * client-side (docs/crm-outreach-lists-plan.md §B3: the server classifies once; toggles, the split and
 * the summary are recomputed here). React-free and axios-free (do NOT import ../lib — it pulls in the
 * api client), so the tests need no mocks. The workQueueLogic / safetyNetLane precedent.
 *
 * The send-time walk (`planSendTimes`) MUST match backend/src/services/crmOutreachLogic.js
 * `planSendTimes` exactly — both sides are pinned by the same worked examples in their tests.
 */
import type {
  OutreachCandidate,
  OutreachExclusion,
  OutreachFlag,
  OutreachMemberBucket,
  OutreachOutcome,
  OutreachLoggableOutcome,
  OutreachMessageMode,
  OutreachTextBlock,
} from './types';

// ---------------------------------------------------------------------------------------------
// Words
// ---------------------------------------------------------------------------------------------

export const EXCLUSION_LABELS: Record<OutreachExclusion, string> = {
  merged: 'Merged into another record',
  enrolled_or_scheduled: 'Scheduled, screened or enrolled in a study',
  already_on_study: 'Already on this study',
  busy_other_study: 'Being worked for another open study',
  in_agent_conversation: 'Texting with the agent right now',
  asked_to_stop: 'Asked us not to contact them',
  wrong_number: 'Wrong number on file',
  opted_out_sms: 'Opted out of texts',
  no_phone: 'No phone number',
  on_other_list: 'Already on another outreach list',
  recently_contacted: 'Contacted recently',
};

/** Why each exclusion exists — the one-line hint under the count. */
export const EXCLUSION_HINTS: Partial<Record<OutreachExclusion, string>> = {
  enrolled_or_scheduled: 'Never added — someone in or headed into a study is never recruited for another.',
  already_on_study: "Their history on this study stays as it is.",
  busy_other_study: 'Another open study is working with them now.',
  asked_to_stop: 'They told us to stop — they are never added.',
  opted_out_sms: 'They texted STOP.',
};

export const TEXT_BLOCK_LABELS: Record<OutreachTextBlock, string> = {
  soft_stopped: 'Told us to stop texting',
  no_consent_basis: 'Imported or added by hand, and never texted us',
  too_old: 'Last heard from us over a year ago',
  prior_not_interested: 'Said they were not interested last time',
  agent_skipped: 'The agent skipped them',
  not_text_mode: 'No first message on this list',
  cancelled_by_admin: 'Texts were stopped by an admin',
};

export const FLAG_LABELS: Record<OutreachFlag, string> = {
  prior_not_eligible: 'Was not eligible last time',
  prior_not_interested: 'Said not interested last time',
  prior_unreachable: "Couldn't be reached last time",
  was_archived: 'Was archived',
  unanswered_reply: 'Had an unanswered reply',
  soft_stopped: "Told us to stop texting — don't text",
  old_lead: 'Last heard from us a long time ago',
  resume_on_study: 'Picking up where they left off on this study',
};

/** Outcomes a recruiter logs, in menu order. `primary` is the split button's main action. */
export const OUTCOME_MENU: Array<{ value: OutreachLoggableOutcome; label: string; primary?: boolean; finishes: boolean }> = [
  { value: 'no_answer', label: 'No answer', primary: true, finishes: false },
  { value: 'voicemail', label: 'Left voicemail', finishes: false },
  { value: 'texted', label: 'Sent a text', finishes: false },
  { value: 'call_back', label: 'Call back at…', finishes: false },
  { value: 'try_later', label: 'Try later…', finishes: false },
  { value: 'interested', label: 'Interested', finishes: true },
  { value: 'not_interested', label: 'Not interested', finishes: true },
  { value: 'wrong_number', label: 'Wrong number', finishes: true },
  { value: 'asked_to_stop', label: 'Asked us not to contact them', finishes: true },
];

export const OUTCOME_LABELS: Record<OutreachOutcome, string> = {
  no_answer: 'No answer',
  voicemail: 'Left voicemail',
  texted: 'Sent a text',
  call_back: 'Call back booked',
  try_later: 'Try later',
  interested: 'Interested',
  not_interested: 'Not interested',
  wrong_number: 'Wrong number',
  asked_to_stop: 'Asked us not to contact them',
  no_response: 'No response after all tries',
  advanced: 'Moved ahead',
  closed_elsewhere: 'Closed on the lead page',
  moved_study: 'Moved to another study',
  removed: 'Removed',
  changed_during_launch: 'Changed while the list was being set up',
  list_closed: 'List closed',
};

/**
 * Label for a member's current state. `now` is injected so tests are deterministic. A null bucket is
 * an open member the launch hasn't prepared yet (the members page lists them while it runs).
 */
export function bucketLabel(
  bucket: OutreachMemberBucket | null,
  m: { attempts?: number; max_attempts?: number; waiting_until?: string | null; outcome?: OutreachOutcome | null },
  fmt: (iso: string) => string = shortWhen,
): string {
  if (bucket == null) return 'Being added to the list';
  const tries = `Try ${Math.min((m.attempts ?? 0) + 1, m.max_attempts ?? 3)} of ${m.max_attempts ?? 3}`;
  switch (bucket) {
    case 'replied': return 'Replied — waiting on us';
    case 'callback_due': return 'Call back now';
    case 'agent_review': return 'The agent needs you';
    case 'untouched': return 'Not called yet';
    case 'contacted_no_outcome': return 'Contacted — no outcome logged';
    case 'retry': return tries;
    case 'waiting_retry': return m.waiting_until ? `Next try ${fmt(m.waiting_until)}` : 'Waiting for the next try';
    case 'waiting_callback': return m.waiting_until ? `Call back ${fmt(m.waiting_until)}` : 'Call back booked';
    case 'waiting_text': return 'Text scheduled';
    case 'waiting_after_text': return m.waiting_until ? `Texted — call after ${fmt(m.waiting_until)}` : 'Texted — give them time to reply';
    case 'waiting_agent': return m.waiting_until ? `Agent texted — waiting for a reply until ${fmt(m.waiting_until)}` : 'Agent texted — waiting for a reply';
    case 'done': return m.outcome ? OUTCOME_LABELS[m.outcome] : 'Finished';
    case 'removed': return m.outcome ? OUTCOME_LABELS[m.outcome] : 'Removed';
    default: return 'Needs attention';
  }
}

/** Buckets a recruiter is served right now (the rest are waiting or finished). */
export const SERVABLE_BUCKETS: ReadonlySet<OutreachMemberBucket> = new Set([
  'replied', 'callback_due', 'agent_review', 'untouched', 'contacted_no_outcome', 'retry',
]);

export const PRIOR_STAGE_LABELS: Record<string, string> = {
  new: 'New',
  contacting: 'Contacting',
  qualified_pending_records: 'Qualified — waiting on records',
  ready_to_schedule: 'Ready to schedule',
  not_eligible: 'Not eligible',
  not_interested: 'Not interested',
  unreachable: 'Unreachable',
  screen_failed: 'Screen failed',
};

/** "Tue 10:00" / "Sep 30, 10:00" — compact local rendering. */
export function shortWhen(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const days = (d.getTime() - now.getTime()) / 86400000;
  if (days > -1 && days < 6) {
    const sameDay = d.toDateString() === now.toDateString();
    return sameDay ? `today ${time}` : `${d.toLocaleDateString(undefined, { weekday: 'short' })} ${time}`;
  }
  return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}, ${time}`;
}

// ---------------------------------------------------------------------------------------------
// The wizard's client-side summary
// ---------------------------------------------------------------------------------------------

export interface WizardOptions {
  recruiterIds: string[];
  mode: OutreachMessageMode;
  /** null = don't skip recently contacted people. */
  recentlyContactedDays: number | null;
  includeNotInterestedTexts: boolean;
  textOldLeadsOverride: boolean;
  oldLeadStopDays: number;
  oldLeadWarnDays: number;
  now: Date;
}

export interface WizardSummary {
  total: number;
  included: OutreachCandidate[];
  excluded: Partial<Record<OutreachExclusion, number>>;
  excludedTotal: number;
  /** recruiterId → how many they get. */
  split: Record<string, number>;
  ownerChanges: number;
  reopened: number;
  reopenedByPrior: Record<string, number>;
  resumed: number;
  textPlanned: number;
  notTexted: Partial<Record<OutreachTextBlock, number>>;
  oldLeadWarn: number;
  stoppedAutomations: number;
}

/** Position order = freshest first (greatest of last in/out/created), stable on contact id. */
export function orderCandidates(cands: OutreachCandidate[]): OutreachCandidate[] {
  return [...cands].sort((a, b) => {
    const ta = a.freshness_at ? Date.parse(a.freshness_at) : 0;
    const tb = b.freshness_at ? Date.parse(b.freshness_at) : 0;
    if (tb !== ta) return tb - ta;
    return a.contact_id < b.contact_id ? -1 : a.contact_id > b.contact_id ? 1 : 0;
  });
}

/** Round-robin in recruiter order over the ordered list → even ±1, each gets a fresh/old mix. */
export function splitRoundRobin(orderedIds: string[], recruiterIds: string[]): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  recruiterIds.forEach((r) => { out[r] = []; });
  if (recruiterIds.length === 0) return out;
  orderedIds.forEach((id, i) => { out[recruiterIds[i % recruiterIds.length]].push(id); });
  return out;
}

const REOPEN_STAGES = new Set(['not_eligible', 'not_interested', 'unreachable', 'screen_failed']);

/** Server-side exclusion first; then the one toggleable exclusion (recently contacted). */
export function effectiveExclusion(c: OutreachCandidate, o: WizardOptions): OutreachExclusion | null {
  if (c.excluded) return c.excluded;
  if (o.recentlyContactedDays != null && c.last_outbound_at) {
    const ageMs = o.now.getTime() - Date.parse(c.last_outbound_at);
    if (ageMs >= 0 && ageMs < o.recentlyContactedDays * 86400000) return 'recently_contacted';
  }
  return null;
}

/** Every reason a candidate won't get the first message, in the server's order. */
export function candidateTextBlocks(c: Pick<OutreachCandidate, 'text_block' | 'text_blocks'>): OutreachTextBlock[] {
  if (Array.isArray(c.text_blocks)) return c.text_blocks;
  return c.text_block ? [c.text_block] : [];
}

/**
 * Why an included person won't get the first message (null = they will). Walks every block the
 * way create does (backend crmOutreachLogic.effectiveTextBlock): each toggle lifts only its own
 * block, and the first block left is the answer — so a too-old lead who also said not interested
 * is not promised a text because "text old leads" alone was ticked.
 */
export function effectiveTextBlock(c: OutreachCandidate, o: WizardOptions): OutreachTextBlock | null {
  if (o.mode !== 'text' && o.mode !== 'agent') return 'not_text_mode';
  for (const b of candidateTextBlocks(c)) {
    if (b === 'prior_not_interested' && o.includeNotInterestedTexts) continue;
    if (b === 'too_old' && o.textOldLeadsOverride) continue;
    return b;
  }
  return null;
}

export function summarizeWizard(cands: OutreachCandidate[], o: WizardOptions): WizardSummary {
  const excluded: Partial<Record<OutreachExclusion, number>> = {};
  const included: OutreachCandidate[] = [];
  for (const c of cands) {
    const ex = effectiveExclusion(c, o);
    if (ex) { if (ex !== 'merged') excluded[ex] = (excluded[ex] ?? 0) + 1; continue; }
    included.push(c);
  }
  const ordered = orderCandidates(included);
  const split = splitRoundRobin(ordered.map((c) => c.contact_id), o.recruiterIds);
  const assigneeOf = new Map<string, string>();
  Object.entries(split).forEach(([r, ids]) => ids.forEach((id) => assigneeOf.set(id, r)));

  let ownerChanges = 0;
  let reopened = 0;
  let resumed = 0;
  let textPlanned = 0;
  let oldLeadWarn = 0;
  let stoppedAutomations = 0;
  const reopenedByPrior: Record<string, number> = {};
  const notTexted: Partial<Record<OutreachTextBlock, number>> = {};
  for (const c of ordered) {
    const to = assigneeOf.get(c.contact_id);
    if (to && c.owner_user_id !== to) ownerChanges += 1;
    if (c.resume_status) resumed += 1;
    else if (c.archived || (c.prior_lifecycle && REOPEN_STAGES.has(c.prior_lifecycle))) {
      reopened += 1;
      const key = c.archived && !(c.prior_lifecycle && REOPEN_STAGES.has(c.prior_lifecycle)) ? 'archived' : (c.prior_lifecycle as string);
      reopenedByPrior[key] = (reopenedByPrior[key] ?? 0) + 1;
    }
    stoppedAutomations += (c.active_enrollments || 0) + (c.pending_messages || 0);
    if (c.age_days != null && c.age_days > o.oldLeadWarnDays) oldLeadWarn += 1;
    const block = effectiveTextBlock(c, o);
    if (block) { if (block !== 'not_text_mode') notTexted[block] = (notTexted[block] ?? 0) + 1; }
    else textPlanned += 1;
  }
  const splitCounts: Record<string, number> = {};
  Object.entries(split).forEach(([r, ids]) => { splitCounts[r] = ids.length; });
  const excludedTotal = Object.values(excluded).reduce((a, b) => a + (b ?? 0), 0);
  return {
    total: cands.filter((c) => c.excluded !== 'merged').length,
    included: ordered,
    excluded,
    excludedTotal,
    split: splitCounts,
    ownerChanges,
    reopened,
    reopenedByPrior,
    resumed,
    textPlanned,
    notTexted,
    oldLeadWarn,
    stoppedAutomations,
  };
}

// ---------------------------------------------------------------------------------------------
// Message rules (mirrors the server's hard rules — the server is authoritative)
// ---------------------------------------------------------------------------------------------

export const ALLOWED_MESSAGE_FIELDS = ['first_name', 'last_name', 'full_name', 'study_title'] as const;
export const BRAND_NAME = 'West Clinical Research';

export interface MessageLint {
  errors: string[];
  warnings: string[];
  namesStudy: boolean;
  chars: number;
  segments: number;
}

/** GSM-7 vs UCS-2 segment count — the same rule carriers bill by. */
const GSM7_CHARS = new Set(Array.from(
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà^{}\\[~]|€',
));

export function smsSegments(text: string): number {
  if (!text) return 0;
  const gsm = Array.from(text).every((ch) => GSM7_CHARS.has(ch));
  const len = text.length;
  if (gsm) return len <= 160 ? 1 : Math.ceil(len / 153);
  return len <= 70 ? 1 : Math.ceil(len / 67);
}

export function lintMessage(body: string, studyTitle: string | null): MessageLint {
  const text = body ?? '';
  const errors: string[] = [];
  const warnings: string[] = [];
  if (!text.trim()) errors.push('Write the message first.');
  const tokens = Array.from(text.matchAll(/\{\{\s*([^}]+?)\s*\}\}/g)).map((m) => m[1]);
  const unknown = tokens.filter((t) => !(ALLOWED_MESSAGE_FIELDS as readonly string[]).includes(t));
  if (unknown.length) errors.push(`"${unknown[0]}" isn't something we can fill in. Use the Variables list.`);
  // Brand name and STOP wording are warnings, not rules (owner ask 2026-10-08).
  if (text.trim() && !text.toLowerCase().includes(BRAND_NAME.toLowerCase())) {
    warnings.push(`Say who it's from — people ignore texts from numbers they don't know. Include "${BRAND_NAME}".`);
  }
  if (text.trim() && !/\bstop\b/i.test(text)) warnings.push('Tell people how to opt out, like "Reply STOP to opt out."');
  const namesStudy = tokens.includes('study_title')
    || (!!studyTitle && studyTitle.trim().length > 3 && text.toLowerCase().includes(studyTitle.trim().toLowerCase()));
  const segments = smsSegments(text);
  if (segments > 3) warnings.push(`This is ${segments} texts long. Shorter messages get more replies.`);
  return { errors, warnings, namesStudy, chars: text.length, segments };
}

/** Render the preview the way the server will (unknown tokens stay visible so they get fixed). */
export function renderPreview(
  body: string,
  ctx: { first_name?: string | null; last_name?: string | null; study_title?: string | null },
): string {
  const full = [ctx.first_name, ctx.last_name].filter(Boolean).join(' ');
  const values: Record<string, string> = {
    first_name: ctx.first_name || '',
    last_name: ctx.last_name || '',
    full_name: full,
    study_title: ctx.study_title || '',
  };
  return (body ?? '').replace(/\{\{\s*([^}]+?)\s*\}\}/g, (m, k: string) => (k in values ? values[k] : m));
}

export const DEFAULT_MESSAGE =
  // Plain GSM-7 characters only (no em dash / curly quotes): one non-GSM character switches the whole
  // text to UCS-2 and more than doubles the segment count.
  "Hi {{first_name}}, this is the West Clinical Research team. You connected with us a while back, and a new study has opened that may be a fit based on what you shared. Want to hear more? Reply YES and we'll call you at a time that suits you, or we'll try you in the next few days. Reply STOP to opt out.";

// ---------------------------------------------------------------------------------------------
// Send-time walk — identical to backend crmOutreachLogic.planSendTimes
// ---------------------------------------------------------------------------------------------

export const SEND_WINDOW_START_HOUR = 8;  // inclusive, tenant local
export const SEND_WINDOW_END_HOUR = 21;   // exclusive, tenant local (crmCompliance quiet hours)

/** Local wall-clock parts of `d` in `tz`. */
function localParts(d: Date, tz: string): { y: number; mo: number; da: number; h: number; mi: number } {
  const f = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  });
  const p: Record<string, string> = {};
  f.formatToParts(d).forEach((x) => { p[x.type] = x.value; });
  return { y: +p.year, mo: +p.month, da: +p.day, h: +p.hour, mi: +p.minute };
}

/** UTC instant of local `y-mo-da hh:00` in `tz` (DST-safe: two-pass offset correction). */
function zonedInstant(y: number, mo: number, da: number, hh: number, tz: string): Date {
  let guess = Date.UTC(y, mo - 1, da, hh, 0, 0);
  for (let i = 0; i < 2; i += 1) {
    const p = localParts(new Date(guess), tz);
    const asUtc = Date.UTC(p.y, p.mo - 1, p.da, p.h, p.mi, 0);
    guess += Date.UTC(y, mo - 1, da, hh, 0, 0) - asUtc;
  }
  return new Date(guess);
}

/** If `t` is outside [08:00, 21:00) tenant-local, move it to the next 08:00; else return it. */
export function intoSendWindow(t: Date, tz: string): Date {
  const p = localParts(t, tz);
  if (p.h >= SEND_WINDOW_START_HOUR && p.h < SEND_WINDOW_END_HOUR) return t;
  if (p.h < SEND_WINDOW_START_HOUR) return zonedInstant(p.y, p.mo, p.da, SEND_WINDOW_START_HOUR, tz);
  // After 21:00 → 08:00 the next local day (date math in UTC on the local calendar date).
  const next = new Date(Date.UTC(p.y, p.mo - 1, p.da + 1));
  return zonedInstant(next.getUTCFullYear(), next.getUTCMonth() + 1, next.getUTCDate(), SEND_WINDOW_START_HOUR, tz);
}

/**
 * Send times for `count` texts from `start` at `pacePerHour`, never inside quiet hours.
 * slot0 = intoSendWindow(start); slot(k+1) = intoSendWindow(slot(k) + 3600/pace seconds).
 */
export function planSendTimes(count: number, start: Date, pacePerHour: number, tz: string): Date[] {
  const out: Date[] = [];
  if (count <= 0) return out;
  const stepMs = Math.round(3600000 / Math.max(1, Math.min(600, pacePerHour)));
  let t = intoSendWindow(start, tz);
  for (let k = 0; k < count; k += 1) {
    out.push(t);
    t = intoSendWindow(new Date(t.getTime() + stepMs), tz);
  }
  return out;
}

/** Default text pace: 30 per recruiter per hour (clamped 1–600). Agent mode uses the agent's own default. */
export function defaultPace(mode: OutreachMessageMode, recruiters: number, agentDefault: number): number {
  if (mode === 'agent') return agentDefault;
  return Math.max(1, Math.min(600, 30 * Math.max(1, recruiters)));
}
